import Initialiser from 'initialiser';
import { cloneDeep } from 'utils/immutable';
import api from 'utils/api';
import * as _ from 'lodash';
import constants from 'utils/constants';
import { selectUser } from 'ducks/modules/user';
import { pushToast } from 'ducks/modules/toaster';
import store from 'ducks/configureStore';

// ACTION TYPES
const START_FETCH_NOTIFICATIONS = 'START_FETCH_NOTIFICATIONS';
const READ_NOTIFICATION = 'READ_NOTIFICATION';
const READ_ALL_NOTIFICATIONS = 'READ_ALL_NOTIFICATIONS';
const UPDATED_NOTIFICATIONS = 'UPDATED_NOTIFICATIONS';

// SYNC ACTION CREATORS
export function startFetchingNotifications() {
    return { type: START_FETCH_NOTIFICATIONS };
}
export function updatedNotifications(notifications) {
    return { type: UPDATED_NOTIFICATIONS, notifications };
}
export function readNotification(notificationId) {
    return { type: READ_NOTIFICATION, notificationId };
}
export function readAllNotifications() {
    return { type: READ_ALL_NOTIFICATIONS };
}

// ASYNC ACTION CREATORS
export function fetchNotifications(prepend = false, limit = 10, clear = false) {
    // Clear will cause the notifications to be reset with whatever is returned by the call
    // Prepend will be used to fetch for notifications to add before all the notifications already present
    // If both clear and prepend are set to false then the default behaviour is assumed, where new notifications are appended to the existing ones
    const user = store.getState().user;
    // Prevent unauthorized errors while reviewing T&C
    if (!user.accepted_terms) {
        return;
    }
    const items = store.getState().notifications.items;
    const minDate = !clear && prepend && items.length > 0 ? new Date(items[0].createdAt) : null;
    const maxDate = !clear && !prepend && items.length > 0 ? new Date(items[items.length - 1].createdAt) : null;
    return async (dispatch) => {
        if (!clear && !prepend) {
            // Display the loading only if you are trying to append more (continuous scrolling)
            dispatch(startFetchingNotifications());
        }
        try {
            const notifications = await api.fetchNotifications(selectUser().id, minDate, maxDate, limit);
            if (!clear) {
                if (prepend) {
                    // Notifications should be prepended to what has already been fetched
                    notifications.items = notifications.items.concat(store.getState().notifications.items);
                } else {
                    notifications.items = store.getState().notifications.items.concat(notifications.items);
                }
            }
            dispatch(updatedNotifications(notifications));
        } catch {
            dispatch(updatedNotifications({ items: [], status: 'error', next: 0 }));
        }
    };
}

export function setNotificationAsRead(notificationId) {
    return async (dispatch) => {
        try {
            const unread = store
                .getState()
                .notifications.items.find((n) => n.id === notificationId && n.read === false);
            // No need to perform the call if the notification has already been read
            if (unread !== undefined) {
                await api.readNotification(selectUser().id, notificationId);
            }
            dispatch(readNotification(notificationId));
        } catch {
            dispatch(
                pushToast({
                    title: 'Unable to mark notification as read.',
                    type: constants.ToastTypes.ERROR,
                    expires: constants.ToastDurations.SHORT,
                }),
            );
        }
    };
}

export function setAllNotificationsAsRead() {
    return async (dispatch) => {
        try {
            const unread = store.getState().notifications.items.find((n) => !n.read);
            // No need to perform the call if all notifications have been read
            if (unread !== undefined) {
                await api.readAllNotifications(selectUser().id);
            }
            dispatch(readAllNotifications());
        } catch {
            dispatch(
                pushToast({
                    title: 'Unable to mark all notifications as read.',
                    type: constants.ToastTypes.ERROR,
                    expires: constants.ToastDurations.SHORT,
                }),
            );
        }
    };
}

// REDUCER
export default function reducer(state = Initialiser.initialState.notifications, action) {
    switch (action.type) {
        case START_FETCH_NOTIFICATIONS: {
            state.loading = true;
            return cloneDeep(state);
        }
        case UPDATED_NOTIFICATIONS: {
            state.loading = false;
            // Make sure to avoid duplication of notifications in case multiple fetches arrive at the same time
            // NOTE: This is not ideal but otherwise we would need to queue the fetch calls waiting for one to return
            state.items = _.uniqBy(state.items, (n) => n.id);
            return cloneDeep(action.notifications);
        }
        case READ_NOTIFICATION: {
            // Set the notification as read
            state.items.find((n) => n.id === action.notificationId).read = true;
            return cloneDeep(state);
        }
        case READ_ALL_NOTIFICATIONS: {
            // Set all notifications as read
            state.items.forEach((n) => (n.read = true));
            return cloneDeep(state);
        }
        default: {
            return state;
        }
    }
}
